import { useCallback, useEffect, useReducer } from 'react';
import { OMFilter as OMFilterCategory } from 'types/enums';
import type { UserProfile } from 'types/authContext';
import type { CountriesList, OrgData } from 'types/dataContext';
import { getEmptyOrgSelection, getHomeUnitId, orgSelectionReducer } from './useOrgSelection.utils';

export const useOrgSelection = ({
    user, // used to autoselect the user's country and unit
    countries, // The list of countries the user has access to
    orgData, // The org data for the selected country
    selectCountry, // Triggers api call to get org data for the selected country
    selectUnit, // Triggers api call to get PA data for the selected unit
}: {
    user: UserProfile | null;
    countries: CountriesList | null;
    orgData: OrgData | undefined;
    selectCountry: (countryCode: string) => void;
    selectUnit: (unitId: string, type: string) => void;
}) => {
    const [orgSelection, dispatch] = useReducer(orgSelectionReducer, getEmptyOrgSelection());

    const updateOrgSelection = useCallback(
        (category: OMFilterCategory, key: string, type?: string) => {
            if (category === OMFilterCategory.COUNTRY) {
                selectCountry(key);
            }
            if (category === OMFilterCategory.UNIT && type) {
                selectUnit(key, type);
            }
            dispatch({ type: 'updateSelection', orgData, category, key, unitType: type });
        },
        [selectCountry, selectUnit, orgData],
    );

    useEffect(() => {
        // Load countries
        if (countries) {
            const homeCountry = countries?.find(country => country.code === user?.country)?.code;
            dispatch({ type: 'populateCountries', countries, homeCountry });
            if (homeCountry) {
                selectCountry(homeCountry);
            }
        }
    }, [countries, selectCountry, user?.country]);

    useEffect(() => {
        // Load country specific org data
        if (orgData) {
            const homeUnit = orgData.businessUnits.find(({ code }) => code === getHomeUnitId(user?.businessUnit));
            dispatch({ type: 'populateOrgData', orgData, homeUnit: homeUnit?.code });
            if (homeUnit) {
                selectUnit(homeUnit.code, homeUnit.type);
            }
        }
    }, [orgData, selectUnit, user?.businessUnit]);

    return {
        orgSelection,
        updateOrgSelection,
    };
};
